import React, { useState } from 'react';

const INITIAL_BOARD = {
  Backlog: [
    { id: 'T1', title: 'Design new onboarding flow', tag: 'Design', priority: 'High' },
    { id: 'T2', title: 'API rate limiting', tag: 'Backend', priority: 'Medium' },
    { id: 'T3', title: 'Mobile responsive fixes', tag: 'Frontend', priority: 'Low' },
  ],
  'In Progress': [
    { id: 'T4', title: 'Payment gateway integration', tag: 'Backend', priority: 'High' },
    { id: 'T5', title: 'Dashboard analytics charts', tag: 'Frontend', priority: 'Medium' },
  ],
  Review: [
    { id: 'T6', title: 'User auth refactor', tag: 'Backend', priority: 'High' },
  ],
  Done: [
    { id: 'T7', title: 'Setup CI/CD pipeline', tag: 'DevOps', priority: 'High' },
    { id: 'T8', title: 'Write unit tests for billing', tag: 'Testing', priority: 'Medium' },
  ],
};

const COLUMNS = ['Backlog', 'In Progress', 'Review', 'Done'];
const TAG_COLORS = { Design: '#7b1fa2', Backend: '#1565c0', Frontend: '#2e7d32', DevOps: '#e65100', Testing: '#c62828' };
const PRI_COLORS = { High: '#c62828', Medium: '#e65100', Low: '#388e3c' };

export default function DragDropKanban() {
  const [board, setBoard] = useState(INITIAL_BOARD);
  const [dragging, setDragging] = useState(null); // { card, fromCol }
  const [dragOver, setDragOver] = useState(null);
  const [showAddCard, setShowAddCard] = useState(null); // column name
  const [newCard, setNewCard] = useState({ title: '', tag: 'Backend', priority: 'Medium' });
  const [newCardErr, setNewCardErr] = useState('');

  const onDragStart = (card, fromCol) => setDragging({ card, fromCol });
  const onDragOver = (e, col) => { e.preventDefault(); setDragOver(col); };
  const onDrop = (e, toCol) => {
    e.preventDefault();
    if (!dragging || dragging.fromCol === toCol) { setDragging(null); setDragOver(null); return; }
    setBoard(prev => {
      const from = prev[dragging.fromCol].filter(c => c.id !== dragging.card.id);
      const to = [...prev[toCol], dragging.card];
      return { ...prev, [dragging.fromCol]: from, [toCol]: to };
    });
    setDragging(null);
    setDragOver(null);
  };

  const addCard = (col) => {
    if (!newCard.title.trim()) { setNewCardErr('Title required'); return; }
    setBoard(prev => ({
      ...prev,
      [col]: [...prev[col], { id: `T${Date.now()}`, ...newCard }],
    }));
    setNewCard({ title: '', tag: 'Backend', priority: 'Medium' });
    setNewCardErr('');
    setShowAddCard(null);
  };

  const removeCard = (col, id) => setBoard(prev => ({ ...prev, [col]: prev[col].filter(c => c.id !== id) }));

  const totalCards = Object.values(board).flat().length;

  return (
    <div className="eval-section">
      <h2 className="eval-section-title">🗃 Kanban Board <span className="eval-badge-scenario">Drag & Drop / State Manipulation</span></h2>
      <p className="eval-desc">Test: drag cards between columns, add new cards to specific columns, remove cards, track board state.</p>
      <div className="kb-stats">
        {COLUMNS.map(col => (
          <span key={col} className="kb-stat">{col}: <strong>{board[col].length}</strong></span>
        ))}
        <span className="kb-stat">Total: <strong>{totalCards}</strong></span>
      </div>

      <div className="kb-board">
        {COLUMNS.map(col => (
          <div
            key={col}
            className={`kb-column ${dragOver === col ? 'kb-column-dragover' : ''}`}
            onDragOver={e => onDragOver(e, col)}
            onDrop={e => onDrop(e, col)}
            onDragLeave={() => setDragOver(null)}
          >
            <div className="kb-col-header">
              <span className="kb-col-title">{col}</span>
              <span className="kb-col-count">{board[col].length}</span>
            </div>

            <div className="kb-cards">
              {board[col].map(card => (
                <div
                  key={card.id}
                  className={`kb-card ${dragging?.card.id === card.id ? 'kb-card-dragging' : ''}`}
                  draggable
                  onDragStart={() => onDragStart(card, col)}
                  onDragEnd={() => { setDragging(null); setDragOver(null); }}
                >
                  <div className="kb-card-title">{card.title}</div>
                  <div className="kb-card-meta">
                    <span className="kb-tag" style={{ background: TAG_COLORS[card.tag] || '#555' }}>{card.tag}</span>
                    <span className="kb-priority" style={{ color: PRI_COLORS[card.priority] }}>● {card.priority}</span>
                    <button className="kb-del-btn" onClick={() => removeCard(col, card.id)} title="Remove">✕</button>
                  </div>
                </div>
              ))}
            </div>

            {showAddCard === col ? (
              <div className="kb-add-form">
                <input
                  autoFocus
                  placeholder="Card title..."
                  value={newCard.title}
                  onChange={e => { setNewCard(c => ({ ...c, title: e.target.value })); setNewCardErr(''); }}
                  className={newCardErr ? 'input-err' : ''}
                />
                {newCardErr && <span className="eval-err">{newCardErr}</span>}
                <div className="kb-add-row">
                  <select value={newCard.tag} onChange={e => setNewCard(c => ({ ...c, tag: e.target.value }))}>
                    {Object.keys(TAG_COLORS).map(t => <option key={t}>{t}</option>)}
                  </select>
                  <select value={newCard.priority} onChange={e => setNewCard(c => ({ ...c, priority: e.target.value }))}>
                    {Object.keys(PRI_COLORS).map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div className="kb-add-actions">
                  <button className="eval-btn eval-btn-primary eval-btn-sm" onClick={() => addCard(col)}>Add</button>
                  <button className="eval-btn eval-btn-sm" onClick={() => { setShowAddCard(null); setNewCardErr(''); }}>Cancel</button>
                </div>
              </div>
            ) : (
              <button className="kb-add-btn" onClick={() => setShowAddCard(col)}>+ Add card</button>
            )}
          </div>
        ))}
      </div>
      <p className="eval-desc" style={{marginTop:8}}>💡 Drag cards between columns. Use the ✕ button to remove cards. Click "+ Add card" to add to a specific column.</p>
    </div>
  );
}
